import React, { useEffect, useMemo, useState } from "react";
import { Button, Card, Col, DatePicker, Form, Input, message, Row, Select, Skeleton, Space, Tag, TimePicker, Tooltip } from "antd";
import { ClearOutlined, FilterOutlined, InfoCircleOutlined } from "@ant-design/icons";
import { useDispatch, useSelector } from "react-redux";
import { CancelButton, FilterComponent, InputText, InputTextArea, ModalComponent, SaveButton } from "@comps/components";
import { makeRequest, makeRequestStateless } from "@utils/helpers";
import dayjs from "dayjs";
import {
  createReportScheduleReq,
  getFilterReq,
  getInitialDataReq,
  getReportScheduleResourcesReq,
  updateReportScheduleReq
} from "../requests";
import { clearFilterOptions } from "../../../../redux/filterOptionsSlice";
import { makeFieldLabel } from "@utils/fieldTooltips";
import { reportTooltips } from "../../common/reportTooltips";

const formName = "scheduleReport";

const fieldLabel = makeFieldLabel(reportTooltips.schedule, { namespace: "schedule" });

const frequencyOptions = [
  { value: "daily", label: "Daily" },
  { value: "weekly", label: "Weekly" },
  { value: "monthly", label: "Monthly" },
  { value: "custom", label: "Custom Cron" },
];

const dayOptions = ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday", "Sunday"]
  .map((day) => ({ value: day, label: day }));

const fileTypeOptions = [
  { value: "pdf", label: "PDF" },
  { value: "xlsx", label: "XLSX" },
  { value: "csv", label: "CSV" },
];

const statusOptions = [
  { value: "active", label: "Active" },
  { value: "inactive", label: "Inactive" },
];

const defaultDateRanges = [
  { value: "today", label: "Today" },
  { value: "yesterday", label: "Yesterday" },
  { value: "current_week", label: "Current Week" },
  { value: "previous_week", label: "Previous Week" },
  { value: "current_month", label: "Current Month" },
  { value: "previous_month", label: "Previous Month" },
  { value: "payroll_month", label: "Payroll Month" },
  { value: "custom", label: "Custom Date Range" },
];

const ScheduleReport = ({ onCompleted, record, schedule, resources: initialResources, initialFilters = {}, scope, disabled = false }) => {
  const dispatch = useDispatch();
  const authUser = useSelector((state) => state.auth.authUser);
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!initialResources);
  const [resources, setResources] = useState(initialResources || {});
  const [filterFields, setFilterFields] = useState([]);
  const [filterApiPayload, setFilterApiPayload] = useState(null);
  const [reportFilterString, setReportFilterString] = useState("");
  const [showReportFilters, setShowReportFilters] = useState(false);
  const frequency = Form.useWatch("frequency", form);
  const dateRangePreset = Form.useWatch("date_range_preset", form);
  const watchedReportId = Form.useWatch("report_id", form);

  const isEdit = Boolean(schedule?.id);
  const fixedReport = record || schedule?.report || null;

  useEffect(() => {
    if (!initialResources) {
      makeRequestStateless(getReportScheduleResourcesReq, { scope }, (data) => {
        setResources(data || {});
        setLoading(false);
      }, () => {
        setLoading(false);
        message.error("Unable to load schedule options");
      });
    }
  }, [initialResources, scope]);

  useEffect(() => {
    form.setFieldsValue(buildInitialValues());
    setReportFilterString(buildFilterString(schedule?.filters || initialFilters || {}, true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [schedule, record]);

  useEffect(() => {
    const reportId = watchedReportId || fixedReport?.id || schedule?.report_id;
    if (!reportId) {
      setFilterFields([]);
      setFilterApiPayload(null);
      return;
    }

    makeRequestStateless(getInitialDataReq, { id: reportId }, prepareReportFilters, () => {
      setFilterFields([]);
      setFilterApiPayload(null);
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [watchedReportId, fixedReport?.id, schedule?.report_id]);

  const reportOptions = useMemo(() => {
    return (resources.reports || []).map((report) => ({ value: report.id, label: report.title }));
  }, [resources.reports]);

  const employmentOptions = useMemo(() => {
    return uniqueBy(resources.employments || [], (employment) => String(employment.id)).map((employment) => ({
      value: employment.id,
      label: `${employment.name}${employment.email ? ` (${employment.email})` : ""}`,
    }));
  }, [resources.employments]);

  const dateRangeOptions = resources.date_range_presets || defaultDateRanges;
  const selectedFilterCount = useMemo(() => {
    return reportFilterString ? reportFilterString.split(";").filter(Boolean).length : 0;
  }, [reportFilterString]);
  const filterHelpText = filterFields.length
    ? (selectedFilterCount > 0 ? `${selectedFilterCount} optional filter${selectedFilterCount > 1 ? "s" : ""} applied` : "No optional filters applied")
    : "No optional filters";
  const filterTooltipText = filterFields.length
    ? "Filters are optional. Leave them empty to send the report for the selected date range and all permitted employees."
    : "No optional filters are configured for this report.";

  function prepareReportFilters(data) {
    const reportDirectiveFilters = data?.report?.directive_filters || data?.report?.directiveFilters || [];
    const preparedFilters = [];
    let directiveFilters = "";
    let tempGroup = "";

    reportDirectiveFilters
      .filter((each) => !["from", "to"].includes(each.name))
      .forEach((each) => {
        let urlDataKey = each.name;
        let optionDataKey = each.name;

        if (each.name === "employees" || each.name === "employee") {
          urlDataKey = "employments";
          optionDataKey = urlDataKey;
        }

        if (each.group !== tempGroup) {
          directiveFilters += `${each.group}_|${urlDataKey};`;
        } else {
          const lastIndex = directiveFilters.lastIndexOf(";");
          if (lastIndex === directiveFilters.length - 1) {
            directiveFilters = directiveFilters.slice(0, lastIndex);
          }
          directiveFilters += `,${urlDataKey};`;
        }

        tempGroup = each.group;

        let fieldType = "select";
        const dataKey = each.group.replace("fields", "data");
        if (each.group === "date_time_fields") {
          fieldType = "date";
        }

        const config = normalizeDirectiveConfig(each.config);

        preparedFilters.push({
          type: fieldType,
          placeholder: titleize(each.name),
          key: each.name,
          data_key: `${dataKey}.${optionDataKey}`,
          multiple: !!config?.multiple,
          required: false,
          resource_type: "employment"
        });
      });

    setFilterFields(preparedFilters);
    setFilterApiPayload({
      page: 1,
      filters: arrangeItems(directiveFilters)
    });
  }

  function buildInitialValues() {
    const emailRow = schedule?.email_recipients?.[0] || schedule?.emails || {};
    const timeValue = parseTimeValue(schedule?.time_of_day);
    const reportId = fixedReport?.id || schedule?.report_id;
    const reportTitle = fixedReport?.title || schedule?.report?.title || "Report";

    const storedFilters = schedule?.filters || initialFilters || {};

    return {
      report_id: reportId,
      name: schedule?.name || `${reportTitle} Schedule`,
      file_type: schedule?.file_type || "xlsx",
      date_range_preset: schedule?.date_range_preset || (storedFilters.from && storedFilters.to ? "custom" : "today"),
      custom_date_range: buildCustomDateRangeValue(storedFilters),
      frequency: schedule?.frequency || "daily",
      time_of_day: timeValue,
      timezone: schedule?.timezone || "Asia/Karachi",
      day_of_week: schedule?.day_of_week || "Monday",
      day_of_month: schedule?.day_of_month || 1,
      custom_cron_expression: schedule?.custom_cron_expression || "",
      status: schedule?.status || "active",
      employment_ids: uniqueValues((schedule?.recipients || []).map((employment) => employment.id)),
      email_to: normalizeEmailValue(emailRow.email_to || schedule?.email_to),
      email_cc: normalizeEmailValue(emailRow.email_cc || schedule?.email_cc),
      subject: schedule?.subject || "{report_name} - {date}",
      body: schedule?.body || "Please find the scheduled HRMS report attached.",
    };
  }

  function normalizeEmailValue(value) {
    if (!value) return [];
    if (Array.isArray(value)) return uniqueEmailValues(value);
    try {
      const decoded = JSON.parse(value);
      return Array.isArray(decoded) ? uniqueEmailValues(decoded) : [];
    } catch (e) {
      return uniqueEmailValues(String(value).split(/[,;\s]+/));
    }
  }

  function parseTimeValue(value) {
    if (!value) return dayjs().hour(9).minute(0).second(0);
    const timeMatch = String(value).match(/(?:T|\s|^)(\d{1,2}):(\d{2})(?::(\d{2}))?/);
    if (timeMatch) {
      return dayjs()
        .hour(Number(timeMatch[1]))
        .minute(Number(timeMatch[2]))
        .second(Number(timeMatch[3] || 0));
    }

    const parsed = dayjs(value);
    if (parsed.isValid()) {
      return parsed;
    }

    return dayjs().hour(9).minute(0).second(0);
  }

  function buildCustomDateRangeValue(filters) {
    if (!filters?.from || !filters?.to) return null;
    const from = dayjs(filters.from);
    const to = dayjs(filters.to);
    return from.isValid() && to.isValid() ? [from, to] : null;
  }

  const onSubmit = (values) => {
    const filters = {
      ...parseFilterString(reportFilterString),
    };
    if (values.date_range_preset === "custom" && values.custom_date_range?.length === 2) {
      filters.from = values.custom_date_range[0].format("YYYY-MM-DD");
      filters.to = values.custom_date_range[1].format("YYYY-MM-DD");
    } else {
      delete filters.from;
      delete filters.to;
    }

    const payload = {
      ...values,
      // `timezone` is reserved by the auth middleware for the logged-in user's
      // context. Keep the schedule setting separate so it is not overwritten.
      schedule_timezone: values.timezone?.trim() || "Asia/Karachi",
      // report_schedules.time_of_day is stored as a DATETIME. Sending only
      // HH:mm:ss makes MySQL coerce the value to midnight during an update.
      time_of_day: values.time_of_day?.format ? values.time_of_day.format("YYYY-MM-DD HH:mm:ss") : values.time_of_day,
      day_of_month: values.day_of_month ? Number(values.day_of_month) : values.day_of_month,
      employment_ids: uniqueValues(values.employment_ids || []),
      email_to: uniqueEmailValues(values.email_to || []),
      email_cc: uniqueEmailValues(values.email_cc || []),
      employment_id: authUser?.employment_id,
      filters,
    };
    delete payload.custom_date_range;

    const request = isEdit ? updateReportScheduleReq : createReportScheduleReq;
    if (isEdit) {
      payload.id = schedule.id;
    }

    makeRequest(setSaving, request, payload, () => {
      message.success(isEdit ? "Schedule updated" : "Schedule created");
      onCompleted(true);
    }, (err, res) => {
      message.error(res?.response?.data?.message || "Unable to save schedule");
    });
  };

  const footer = [
    !disabled && <SaveButton loading={ saving } form={ formName } key="save_button" htmlType="submit" />,
    <CancelButton key="close_button" onClick={ () => onCompleted(false) } />
  ];

  return (
    <ModalComponent top={ 20 } width={ 960 } maskClosable={ false }
      mainTitle={ isEdit ? "Edit Schedule" : "Schedule Report" } subTitle={ fixedReport?.title || schedule?.report?.title || "" }
      visible={ true } footer={ footer } onCancel={ () => onCompleted(false) }>
      <Skeleton loading={ loading }>
        <Form { ...formLayout } name={ formName } onFinish={ onSubmit } form={ form } disabled={ disabled }>
          <Row gutter={ 20 }>
            <Col lg={ 12 } sm={ 24 } xs={ 24 }>
              <Form.Item name="report_id" label={ fieldLabel("report_id", "Report") } rules={ [{ required: true, message: "Please select report" }] }>
                <Select
                  showSearch
                  disabled={ Boolean(fixedReport) || isEdit }
                  options={ fixedReport ? [{ value: fixedReport.id, label: fixedReport.title }] : reportOptions }
                  optionFilterProp="label"
                  placeholder="Select report"
                />
              </Form.Item>
            </Col>
            <Col lg={ 12 } sm={ 24 } xs={ 24 }>
              <Form.Item name="name" label={ fieldLabel("name", "Schedule Name") } rules={ [{ required: true, message: "Please enter schedule name" }] }>
                <InputText placeholder="Daily attendance to HR" />
              </Form.Item>
            </Col>
            <Col lg={ 8 } sm={ 24 } xs={ 24 }>
              <Form.Item name="file_type" label={ fieldLabel("file_type", "File Type") } rules={ [{ required: true, message: "Please select file type" }] }>
                <Select options={ fileTypeOptions } />
              </Form.Item>
            </Col>
            <Col lg={ 8 } sm={ 24 } xs={ 24 }>
              <Form.Item
                name="date_range_preset"
                label={ fieldLabel("date_range_preset", <Space size={ 4 }>
                    Date Range
                    <Tooltip title="This controls the report period. Use Optional Report Filters for department, branch, employee, or other report-specific filters.">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>) }
                rules={ [{ required: true, message: "Please select date range" }] }
              >
                <Select options={ dateRangeOptions } />
              </Form.Item>
            </Col>
            { dateRangePreset === "custom" && (
              <Col lg={ 8 } sm={ 24 } xs={ 24 }>
                <Form.Item
                  name="custom_date_range"
                  label={ fieldLabel("custom_date_range", "From / To") }
                  rules={ [{ required: true, message: "Please select report date range" }] }
                >
                  <DatePicker.RangePicker style={ { width: "100%" } } />
                </Form.Item>
              </Col>
            ) }
            <Col lg={ 8 } sm={ 24 } xs={ 24 }>
              <Form.Item name="status" label={ fieldLabel("status", "Status") } rules={ [{ required: true, message: "Please select status" }] }>
                <Select options={ statusOptions } />
              </Form.Item>
            </Col>
            <Col lg={ 8 } sm={ 24 } xs={ 24 }>
              <Form.Item
                name="frequency"
                label={ fieldLabel("frequency", <Space size={ 4 }>
                    Repeat
                    <Tooltip title="Choose how often this report should be queued and emailed.">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>) }
                rules={ [{ required: true, message: "Please select frequency" }] }
              >
                <Select options={ frequencyOptions } />
              </Form.Item>
            </Col>
            { frequency !== "custom" && (
              <Col lg={ 8 } sm={ 24 } xs={ 24 }>
                <Form.Item
                  name="time_of_day"
                  label={ fieldLabel("time_of_day", <Space size={ 4 }>
                      Send Time
                      <Tooltip title="The report is queued at this time. Email is sent after the file is generated.">
                        <InfoCircleOutlined />
                      </Tooltip>
                    </Space>) }
                  rules={ [{ required: true, message: "Please select send time" }] }
                >
                  <TimePicker style={ { width: "100%" } } format="hh:mm A" use12Hours />
                </Form.Item>
              </Col>
            ) }
            <Col lg={ 8 } sm={ 24 } xs={ 24 }>
              <Form.Item
                name="timezone"
                label={ fieldLabel("timezone", <Space size={ 4 }>
                    Timezone
                    <Tooltip title="Used to calculate the schedule time and date range.">
                      <InfoCircleOutlined />
                    </Tooltip>
                  </Space>) }
                rules={ [{ required: true, message: "Please enter timezone" }] }
              >
                <Input placeholder="Asia/Karachi" />
              </Form.Item>
            </Col>
            { frequency === "weekly" && (
              <Col lg={ 8 } sm={ 24 } xs={ 24 }>
                <Form.Item name="day_of_week" label={ fieldLabel("day_of_week", "Day of Week") } rules={ [{ required: true, message: "Please select day" }] }>
                  <Select options={ dayOptions } />
                </Form.Item>
              </Col>
            ) }
            { frequency === "monthly" && (
              <Col lg={ 8 } sm={ 24 } xs={ 24 }>
                <Form.Item name="day_of_month" label={ fieldLabel("day_of_month", "Day of Month") } rules={ [{ required: true, message: "Please enter day" }] }>
                  <Input type="number" min={ 1 } max={ 31 } />
                </Form.Item>
              </Col>
            ) }
            { frequency === "custom" && (
              <Col lg={ 16 } sm={ 24 } xs={ 24 }>
                <Form.Item name="custom_cron_expression" label={ fieldLabel("custom_cron_expression", "Cron Expression") } rules={ [{ required: true, message: "Please enter cron expression" }] }>
                  <InputText placeholder="0 9 * * 1" />
                </Form.Item>
              </Col>
            ) }
            <Col lg={ 24 } sm={ 24 } xs={ 24 }>
              <Card size="small" style={ { borderRadius: 8, background: "#fafafa" } }>
                <Space direction="vertical" size={ 8 } style={ { width: "100%" } }>
                  <Space wrap>
                    <Tooltip title={ filterTooltipText }>
                      <span>
                        <Button
                          icon={ <FilterOutlined /> }
                          disabled={ !filterFields.length }
                          onClick={ () => {
                            dispatch(clearFilterOptions());
                            setShowReportFilters(true);
                          } }
                        >
                          Optional Report Filters
                        </Button>
                      </span>
                    </Tooltip>
                    <Tooltip title={ filterTooltipText }>
                      <InfoCircleOutlined />
                    </Tooltip>
	                    { selectedFilterCount > 0 && <Tag color="blue">{ selectedFilterCount } applied</Tag> }
                    { selectedFilterCount > 0 && (
                      <Button size="small" icon={ <ClearOutlined /> } onClick={ () => setReportFilterString("") }>
                        Clear Filters
                      </Button>
                    ) }
                  </Space>
	                  <span style={ { color: "#666" } }>{ filterHelpText }</span>
                </Space>
              </Card>
            </Col>
            <Col lg={ 24 } sm={ 24 } xs={ 24 }>
              <Form.Item
                name="employment_ids"
                label={ fieldLabel("employment_ids", "Internal Recipients") }
                dependencies={ ["email_to"] }
                rules={ [{ validator: validateRecipients }] }
              >
                <Select
                  mode="multiple"
                  showSearch
                  options={ employmentOptions }
                  optionFilterProp="label"
                  maxTagCount="responsive"
                  placeholder="Select HR managers, owners, payroll users"
                />
              </Form.Item>
            </Col>
            <Col lg={ 12 } sm={ 24 } xs={ 24 }>
              <Form.Item name="email_to" label={ fieldLabel("email_to", "Manual Email To") } dependencies={ ["employment_ids"] } rules={ [{ validator: validateEmailTags }] }>
                <Select mode="tags" tokenSeparators={ [",", ";", " "] } maxTagCount="responsive" placeholder="owner@company.com" />
              </Form.Item>
            </Col>
            <Col lg={ 12 } sm={ 24 } xs={ 24 }>
              <Form.Item name="email_cc" label={ fieldLabel("email_cc", "CC") } rules={ [{ validator: validateEmailTags }] }>
                <Select mode="tags" tokenSeparators={ [",", ";", " "] } maxTagCount="responsive" placeholder="accountant@company.com" />
              </Form.Item>
            </Col>
            <Col lg={ 24 } sm={ 24 } xs={ 24 }>
              <Form.Item name="subject" label={ fieldLabel("subject", "Email Subject") } rules={ [{ required: true, message: "Please enter subject" }] }>
                <InputText placeholder="{report_name} - {date}" />
              </Form.Item>
            </Col>
            <Col lg={ 24 } sm={ 24 } xs={ 24 }>
              <Form.Item name="body" label={ fieldLabel("body", "Email Body") } rules={ [{ required: true, message: "Please enter email body" }] }>
                <InputTextArea rows={ 3 } placeholder="Please find the scheduled HRMS report attached." />
              </Form.Item>
            </Col>
          </Row>
        </Form>
      </Skeleton>
      { showReportFilters && filterFields.length > 0 && filterApiPayload && (
        <FilterComponent
          filters={ filterFields }
          selected={ reportFilterString }
          api={ () => getFilterReq(filterApiPayload) }
          onFilter={ setReportFilterString }
          onCloseFilter={ setShowReportFilters }
          module="reports"
        />
      ) }
    </ModalComponent>
  );

  function validateRecipients() {
    const employmentIds = form.getFieldValue("employment_ids") || [];
    const emailTo = form.getFieldValue("email_to") || [];
    if (employmentIds.length || emailTo.length) {
      return Promise.resolve();
    }
    return Promise.reject(new Error("Select at least one internal recipient or manual email"));
  }

  function validateEmailTags(_, value = []) {
    const invalid = value.find((email) => !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(String(email).trim()));
    if (invalid) {
      return Promise.reject(new Error(`Invalid email: ${invalid}`));
    }
    return Promise.resolve();
  }
};

export default ScheduleReport;

function normalizeDirectiveConfig(config) {
  if (!config) {
    return {};
  }

  if (typeof config === "string") {
    try {
      return JSON.parse(config);
    } catch (error) {
      return {};
    }
  }

  return typeof config === "object" ? config : {};
}

function titleize(value) {
  return String(value || "")
    .replace(/_/g, " ")
    .replace(/\b\w/g, (char) => char.toUpperCase());
}

function parseFilterString(filterString) {
  if (!filterString) {
    return {};
  }

  return filterString
    .split(";")
    .filter(Boolean)
    .reduce((acc, pair) => {
      const [key, value = ""] = pair.split("_|");
      if (key) {
        acc[key] = value;
      }
      return acc;
    }, {});
}

function buildFilterString(filters, excludeDateRange = false) {
  return Object.entries(filters || {})
    .filter(([key, value]) => value !== undefined && value !== null && value !== "" && (!excludeDateRange || !["from", "to"].includes(key)))
    .map(([key, value]) => `${key}_|${Array.isArray(value) ? value.join(",") : value}`)
    .join(";");
}

function uniqueBy(items, getKey) {
  const seen = new Set();
  return (items || []).filter((item) => {
    const key = getKey(item);
    if (!key || seen.has(key)) {
      return false;
    }

    seen.add(key);
    return true;
  });
}

function uniqueValues(values) {
  return uniqueBy(values || [], (value) => String(value));
}

function uniqueEmailValues(values) {
  return uniqueValues((values || [])
    .map((email) => String(email).trim())
    .filter(Boolean));
}

function arrangeItems(input) {
  if (!input.trim()) {
    return "";
  }

  const groups = input.split(";");
  const groupedItems = {};

  groups.forEach((group) => {
    const [fieldType, items] = group.split("_|");
    if (!fieldType || !items) {
      return;
    }

    if (!groupedItems[fieldType]) {
      groupedItems[fieldType] = [];
    }

    groupedItems[fieldType] = groupedItems[fieldType].concat(items.split(","));
  });

  return Object.keys(groupedItems)
    .map((fieldType) => `${fieldType}_|${groupedItems[fieldType].sort().join(",")}`)
    .join(";");
}

const formLayout = {
  layout: "vertical",
  labelCol: { span: 6 },
  wrapperCol: { span: 24 },
  labelAlign: "left"
};
